import {
  Button,
  DocumentList,
  ExpandablePanel,
  ExpandablePanelBody,
  ExpandablePanelHeader,
  InfoPanel,
  TextField,
  Typography,
} from "@king-ict/eupisi-ui/components";
import { hrDate } from "@king-ict/eupisi-ui/utils";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import { useBusinessRule } from "../../../hooks/businessRule/useBusinessRule";
import ApplicantParent from "../../../models/Person/ApplicantParent";
import IntegrationResultStatusEnum from "../../../models/Person/IntegrationResultStatusEnum";
import ParentshipTypeEnum from "../../../models/Person/ParentshipTypeEnum";
import { useApplicantParentDocuments } from "../../Request/ApplicantParentDataStep/useApplicantParentDocuments";
import styles from "./RequestDetailsPage.module.scss";

interface Props {
  emptyValue: string;
  applicantParent: ApplicantParent;
  parentNumber?: string;
  moveToSpecificStep?: (step: number, substep?: number) => void;
  onExpandablePanelClick?: () => void;
}

export default function ApplicantParentDetails({
  emptyValue,
  applicantParent,
  parentNumber,
  moveToSpecificStep,
  onExpandablePanelClick,
}: Props) {
  const { t } = useTranslation();
  const { isApplicantParent } = useBusinessRule();
  const { handleDownloadDocument } = useApplicantParentDocuments();

  return (
    <>
      <Row className={styles.rowSpace}>
        <Col md={12}>
          <ExpandablePanel clickableHeader>
            <ExpandablePanelHeader onClick={onExpandablePanelClick}>
              {t("requestReviewStep.applicantParent")}
            </ExpandablePanelHeader>
            <ExpandablePanelBody>
              <Row className={styles.rowHeadingSpace}>
                <Col md={10}>
                  <Typography variant="smallHeading">
                    {t("person.personalInformation")}
                  </Typography>
                </Col>
                {moveToSpecificStep && (
                  <Col md={2}>
                    <Button
                      size="sm"
                      outline
                      onClick={() => moveToSpecificStep(0, 0)}
                    >
                      {t("common.edit")}
                    </Button>
                  </Col>
                )}
              </Row>
              {isApplicantParent(applicantParent?.oib) ? (
                <>
                  <Row className={styles.rowSpace}>
                    <Col md={4}>
                      <TextField
                        type="text"
                        label={t("person.firstName")}
                        helperText={t("common.birthRegistry")}
                        value={applicantParent?.firstName || emptyValue}
                        plaintext
                      />
                    </Col>
                    <Col md={4}>
                      <TextField
                        type="text"
                        label={t("person.lastName")}
                        helperText={t("common.birthRegistry")}
                        value={applicantParent?.lastName || emptyValue}
                        plaintext
                      />
                    </Col>
                    <Col md={4}>
                      <TextField
                        type="text"
                        label={t("person.oib")}
                        helperText={t("common.oibApi")}
                        value={applicantParent?.oib || emptyValue}
                        plaintext
                      />
                    </Col>
                  </Row>
                  <Row className={styles.rowSpace}>
                    <Col md={4}>
                      <TextField
                        type="text"
                        label={t("person.dateOfBirth")}
                        helperText={t("common.birthRegistry")}
                        value={
                          applicantParent && applicantParent.dateOfBirth
                            ? hrDate(applicantParent?.dateOfBirth)
                            : emptyValue
                        }
                        plaintext
                      />
                    </Col>
                    <Col md={4}>
                      <TextField
                        type="text"
                        label={t("person.sex")}
                        helperText={t("common.birthRegistry")}
                        value={applicantParent?.sexName || emptyValue}
                        plaintext
                      />
                    </Col>
                    <Col md={4}>
                      <TextField
                        type="text"
                        label={t("person.birthplace")}
                        helperText={t("common.birthRegistry")}
                        value={applicantParent?.birthplaceName || emptyValue}
                        plaintext
                      />
                    </Col>
                  </Row>
                  <Row className={styles.rowSpace}>
                    <Col md={4}>
                      <TextField
                        type="text"
                        label={t("person.countryBirth")}
                        helperText={t("common.birthRegistry")}
                        value={
                          applicantParent?.countryOfBirthName || emptyValue
                        }
                        plaintext
                      />
                    </Col>
                    <Col md={4}>
                      <TextField
                        type="text"
                        label={t("person.citizenship")}
                        helperText={t("common.birthRegistry")}
                        value={applicantParent?.citizenshipName || emptyValue}
                        plaintext
                      />
                    </Col>
                    <Col md={4}>
                      <TextField
                        type="text"
                        label={t("person.nationalMinority")}
                        value={
                          applicantParent.nationalMinorityName || emptyValue
                        }
                        plaintext
                      />
                    </Col>
                  </Row>
                  <Row className={styles.rowSpace}>
                    <Col md={4}>
                      <TextField
                        type="text"
                        label={t("person.nationality")}
                        value={applicantParent.nationalityName || emptyValue}
                        plaintext
                      />
                    </Col>
                    <Col md={4}>
                      <TextField
                        type="text"
                        label={t("person.profession")}
                        value={applicantParent.professionName || emptyValue}
                        plaintext
                      />
                    </Col>
                    <Col md={4}>
                      <TextField
                        type="text"
                        label={t("person.professionalQualification")}
                        value={
                          applicantParent.professionalQualificationName ||
                          emptyValue
                        }
                        plaintext
                      />
                    </Col>
                  </Row>
                  <Row className={styles.rowSpace}>
                    <Col md={4}>
                      <TextField
                        type="email"
                        label={t("person.email")}
                        value={applicantParent?.email || emptyValue}
                        plaintext
                      />
                    </Col>
                    <Col md={4}>
                      <TextField
                        type="text"
                        label={t("person.phoneNumber")}
                        value={applicantParent?.phoneNumber || emptyValue}
                        plaintext
                      />
                    </Col>
                    <Col md={4}>
                      <TextField
                        type="text"
                        label={t("person.parentshipType")}
                        value={applicantParent.parentshipTypeName || emptyValue}
                        plaintext
                      />
                    </Col>
                    {parentNumber && (
                      <Col md={4}>
                        <TextField
                          type="text"
                          label={t("person.parentNumber")}
                          value={parentNumber || emptyValue}
                          plaintext
                        />
                      </Col>
                    )}
                  </Row>
                  {applicantParent.parentshipTypeId ===
                    ParentshipTypeEnum.Custodian && (
                    <Row className={styles.rowSpace}>
                      <Col md={4}>
                        <TextField
                          type="text"
                          label={t("person.custodian")}
                          value={applicantParent.custody || emptyValue}
                          plaintext
                        />
                      </Col>
                    </Row>
                  )}
                  <Row className={styles.rowHeadingSpace}>
                    <Col md={12}>
                      <Typography variant="smallHeading">
                        {t("person.permanentResidenceInformation")}
                      </Typography>
                    </Col>
                  </Row>
                  <Row className={styles.rowHeadingSpace}>
                    <Col md={12}>
                      {applicantParent?.integrationResultStatus ? (
                        applicantParent.integrationResultStatus ===
                        IntegrationResultStatusEnum.OibFailed ? (
                          <InfoPanel
                            message={t("person.parentPermanentResidenceError")}
                            color="danger"
                            className={styles.errorMessage}
                          />
                        ) : (
                          <InfoPanel
                            message={t("common.mupResidenceInfoMessage")}
                            color="success"
                          />
                        )
                      ) : null}
                    </Col>
                  </Row>

                  <Row className={styles.rowSpace}>
                    <Col md={4}>
                      <TextField
                        type="text"
                        label={`${t("person.permanentResidence")} - ${t(
                          "person.country"
                        )}`}
                        value={
                          applicantParent?.permanentResidence?.countryName ||
                          emptyValue
                        }
                        plaintext
                      />
                    </Col>
                    <Col md={4}>
                      <TextField
                        type="text"
                        label={`${t("person.permanentResidence")} - ${t(
                          "person.municipality"
                        )}`}
                        value={
                          applicantParent?.permanentResidence
                            ?.municipalityName || emptyValue
                        }
                        plaintext
                      />
                    </Col>
                    <Col md={4}>
                      <TextField
                        type="text"
                        label={`${t("person.permanentResidence")} - ${t(
                          "person.town"
                        )}`}
                        value={
                          applicantParent?.permanentResidence?.townName ||
                          emptyValue
                        }
                        plaintext
                      />
                    </Col>
                  </Row>
                  <Row className={styles.rowSpace}>
                    <Col md={4}>
                      <TextField
                        type="text"
                        label={`${t("person.permanentResidence")} - ${t(
                          "person.postalCode"
                        )}`}
                        value={
                          applicantParent?.permanentResidence?.townPostalCode ||
                          emptyValue
                        }
                        plaintext
                      />
                    </Col>
                    <Col md={4}>
                      <TextField
                        type="text"
                        label={`${t("person.permanentResidence")} - ${t(
                          "person.street"
                        )}`}
                        value={
                          applicantParent?.permanentResidence?.street ||
                          emptyValue
                        }
                        plaintext
                      />
                    </Col>
                    <Col md={4}>
                      <TextField
                        type="text"
                        label={`${t("person.permanentResidence")} - ${t(
                          "person.streetNumber"
                        )}`}
                        value={
                          applicantParent?.permanentResidence?.streetNumber ||
                          emptyValue
                        }
                        plaintext
                      />
                    </Col>
                  </Row>
                  <Row className={styles.rowSpace}>
                    <Col md={4}>
                      <TextField
                        type="text"
                        label={`${t("person.permanentResidence")} - ${t(
                          "person.streetNumberAddition"
                        )}`}
                        value={
                          applicantParent?.permanentResidence
                            ?.streetNumberAddition || emptyValue
                        }
                        plaintext
                      />
                    </Col>
                    <Col md={4}>
                      <TextField
                        type="text"
                        label={`${t("person.permanentResidence")} - ${t(
                          "person.dateFrom"
                        )}`}
                        value={
                          applicantParent?.permanentResidence?.dateFrom
                            ? hrDate(
                                applicantParent.permanentResidence.dateFrom
                              )
                            : emptyValue
                        }
                        plaintext
                      />
                    </Col>
                  </Row>
                  {applicantParent?.temporaryResidence && (
                    <>
                      <Row className={styles.rowHeadingSpace}>
                        <Col md={12}>
                          <Typography variant="smallHeading">
                            {t("person.temporaryResidenceInformation")}
                          </Typography>
                        </Col>
                      </Row>
                      <Row className={styles.rowHeadingSpace}>
                        <Col md={12}>
                          {applicantParent?.integrationResultStatus ? (
                            applicantParent.integrationResultStatus ===
                            IntegrationResultStatusEnum.MupFailed ? (
                              <InfoPanel
                                message={t(
                                  "person.parentTemporaryResidenceError"
                                )}
                                color="danger"
                                className={styles.errorMessage}
                              />
                            ) : (
                              <InfoPanel
                                message={t("common.mupResidenceInfoMessage")}
                                color="success"
                              />
                            )
                          ) : null}
                        </Col>
                      </Row>
                      <Row className={styles.rowSpace}>
                        <Col md={4}>
                          <TextField
                            type="text"
                            label={`${t("person.temporaryResidence")} - ${t(
                              "person.country"
                            )}`}
                            value={
                              applicantParent?.temporaryResidence
                                ?.countryName || emptyValue
                            }
                            plaintext
                          />
                        </Col>
                        <Col md={4}>
                          <TextField
                            type="text"
                            label={`${t("person.temporaryResidence")} - ${t(
                              "person.municipality"
                            )}`}
                            value={
                              applicantParent?.temporaryResidence
                                ?.municipalityName || emptyValue
                            }
                            plaintext
                          />
                        </Col>
                        <Col md={4}>
                          <TextField
                            type="text"
                            label={`${t("person.temporaryResidence")} - ${t(
                              "person.town"
                            )}`}
                            value={
                              applicantParent?.temporaryResidence?.townName ||
                              emptyValue
                            }
                            plaintext
                          />
                        </Col>
                      </Row>
                      <Row className={styles.rowSpace}>
                        <Col md={4}>
                          <TextField
                            type="text"
                            label={`${t("person.temporaryResidence")} - ${t(
                              "person.postalCode"
                            )}`}
                            value={
                              applicantParent?.temporaryResidence
                                ?.townPostalCode || emptyValue
                            }
                            plaintext
                          />
                        </Col>
                        <Col md={4}>
                          <TextField
                            type="text"
                            label={`${t("person.temporaryResidence")} - ${t(
                              "person.street"
                            )}`}
                            value={
                              applicantParent?.temporaryResidence?.street ||
                              emptyValue
                            }
                            plaintext
                          />
                        </Col>
                        <Col md={4}>
                          <TextField
                            type="text"
                            label={`${t("person.temporaryResidence")} - ${t(
                              "person.streetNumber"
                            )}`}
                            value={
                              applicantParent?.temporaryResidence
                                ?.streetNumber || emptyValue
                            }
                            plaintext
                          />
                        </Col>
                      </Row>
                      <Row className={styles.rowSpace}>
                        <Col md={4}>
                          <TextField
                            type="text"
                            label={`${t("person.temporaryResidence")} - ${t(
                              "person.streetNumberAddition"
                            )}`}
                            value={
                              applicantParent?.temporaryResidence
                                ?.streetNumberAddition || emptyValue
                            }
                            plaintext
                          />
                        </Col>
                        <Col md={4}>
                          <TextField
                            type="text"
                            label={`${t("person.temporaryResidence")} - ${t(
                              "person.dateFrom"
                            )}`}
                            value={
                              applicantParent?.temporaryResidence?.dateFrom
                                ? hrDate(
                                    applicantParent.temporaryResidence.dateFrom
                                  )
                                : emptyValue
                            }
                            plaintext
                          />
                        </Col>
                      </Row>
                    </>
                  )}
                  <Row className={styles.rowSpace}>
                    <Col md={12}>
                      <Typography variant="smallHeading">
                        {t("documentUpload.additionalDocumentationList")}
                      </Typography>
                      <DocumentList
                        documents={applicantParent?.documents || []}
                        onDocumentClick={handleDownloadDocument}
                        removable={false}
                        fullWidth
                      />
                    </Col>
                  </Row>
                </>
              ) : (
                <>
                  <Row className={styles.rowSpace}>
                    <Col md={4}>
                      <TextField
                        type="text"
                        label={t("person.firstName")}
                        helperText={t("common.birthRegistry")}
                        value={applicantParent?.firstName || emptyValue}
                        plaintext
                      />
                    </Col>
                    <Col md={4}>
                      <TextField
                        type="text"
                        label={t("person.lastName")}
                        helperText={t("common.birthRegistry")}
                        value={applicantParent?.lastName || emptyValue}
                        plaintext
                      />
                    </Col>
                    <Col md={4}>
                      <TextField
                        type="text"
                        label={t("person.oib")}
                        helperText={t("common.oibApi")}
                        value={applicantParent?.oib || emptyValue}
                        plaintext
                      />
                    </Col>
                  </Row>
                  <Row className={styles.rowSpace}>
                    <Col md={4}>
                      <TextField
                        type="text"
                        label={t("person.yearOfBirth")}
                        value={`${applicantParent?.yearOfBirth}.` || emptyValue}
                        plaintext
                      />
                    </Col>
                    <Col md={4}>
                      <TextField
                        type="text"
                        label={t("person.profession")}
                        value={applicantParent.professionName || emptyValue}
                        plaintext
                      />
                    </Col>
                    <Col md={4}>
                      <TextField
                        type="text"
                        label={t("person.professionalQualification")}
                        value={
                          applicantParent.professionalQualificationName ||
                          emptyValue
                        }
                        plaintext
                      />
                    </Col>
                  </Row>
                  <Row className={styles.rowSpace}>
                    <Col md={4}>
                      <TextField
                        type="text"
                        label={t("person.phoneNumber")}
                        value={applicantParent?.phoneNumber || emptyValue}
                        plaintext
                      />
                    </Col>
                    <Col md={4}>
                      <TextField
                        type="email"
                        label={t("person.email")}
                        value={applicantParent?.email || emptyValue}
                        plaintext
                      />
                    </Col>
                    <Col md={4}>
                      <TextField
                        type="text"
                        label={t("person.parentshipType")}
                        value={applicantParent.parentshipTypeName || emptyValue}
                        plaintext
                      />
                    </Col>
                    {parentNumber && (
                      <Col md={4}>
                        <TextField
                          type="text"
                          label={t("person.parentNumber")}
                          value={parentNumber || emptyValue}
                          plaintext
                        />
                      </Col>
                    )}
                  </Row>
                </>
              )}
            </ExpandablePanelBody>
          </ExpandablePanel>
        </Col>
      </Row>
    </>
  );
}
